import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Quick Order Modal for multi-variant products
 * Shows a table of all variants with quantity selectors
 */
export class QuickOrderModal extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.productId = this.dataset.productId;
    this.setupEventListeners();
  }

  /**
   * Opens the modal
   * @param {HTMLElement} opener - The button that triggered the modal
   */
  show(opener) {
    this.showDialog();
  }

  setupEventListeners() {
    // Quantity increase/decrease buttons
    this.querySelectorAll('[data-action="increase"]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleQuantityChange(e, 1));
    });

    this.querySelectorAll('[data-action="decrease"]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleQuantityChange(e, -1));
    });

    // Direct input changes
    this.querySelectorAll('input[data-variant-qty]').forEach((input) => {
      input.addEventListener('change', () => this.updateTotals());
    });

    // Remove all button
    const removeAllBtn = this.querySelector('[data-action="remove-all"]');
    if (removeAllBtn) {
      removeAllBtn.addEventListener('click', () => this.removeAll());
    }

    // Add to cart button
    const addToCartBtn = this.querySelector('[data-action="add-to-cart"]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }
  }

  handleQuantityChange(e, delta) {
    const row = e.target.closest('.quick-order-row');
    const input = row.querySelector('input[data-variant-qty]');
    const currentValue = parseInt(input.value) || 0;
    const newValue = Math.max(0, currentValue + delta);
    
    input.value = newValue;
    this.updateTotals();
  }

  updateTotals() {
    let totalItems = 0;
    let subtotal = 0;

    this.querySelectorAll('.quick-order-row').forEach((row) => {
      const qty = parseInt(row.querySelector('input[data-variant-qty]').value) || 0;
      const price = parseInt(row.dataset.price);
      const rowTotal = qty * price;

      // Update row total
      const totalCell = row.querySelector('[data-total-cell]');
      if (totalCell) {
        totalCell.textContent = this.formatMoney(rowTotal);
      }

      totalItems += qty;
      subtotal += rowTotal;
    });

    // Update footer totals
    const itemCountElement = this.querySelector('[data-total-items]');
    if (itemCountElement) {
      itemCountElement.textContent = `Selected ${totalItems} total item${totalItems !== 1 ? 's' : ''}`;
    }

    const subtotalElement = this.querySelector('[data-subtotal-amount]');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(subtotal);
    }

    // Enable/disable add to cart button
    const addButton = this.querySelector('[data-action="add-to-cart"]');
    if (addButton) {
      addButton.disabled = totalItems === 0;
    }
  }

  removeAll() {
    this.querySelectorAll('input[data-variant-qty]').forEach((input) => {
      input.value = 0;
    });
    this.updateTotals();
  }

  async handleAddToCart() {
    const updates = {};
    let hasSelections = false;

    this.querySelectorAll('.quick-order-row').forEach((row) => {
      const qty = parseInt(row.querySelector('input[data-variant-qty]').value) || 0;
      if (qty > 0) {
        const variantId = row.dataset.variantId;
        updates[variantId] = qty;
        hasSelections = true;
      }
    });

    if (!hasSelections) {
      alert('Please select at least one item');
      return;
    }

    const addButton = this.querySelector('[data-action="add-to-cart"]');
    addButton.disabled = true;
    addButton.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      if (response.ok) {
        const cartData = await response.json();
        
        // Dispatch CartAddEvent to trigger cart refresh
        document.dispatchEvent(
          new CartAddEvent(cartData, 'quick-order-modal', {
            source: 'quick-order-modal',
            itemCount: cartData.item_count,
          })
        );
        
        this.closeDialog();
      } else {
        alert('Failed to add items to cart. Please try again.');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('An error occurred. Please try again.');
    } finally {
      addButton.disabled = false;
      addButton.textContent = 'Add Items to Cart';
    }
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

customElements.define('quick-order-modal', QuickOrderModal);
